"use client";

import { useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import type { BrowseMode, ListingSummary } from "@/lib/browse/getHomeBrowseData";
import { useLocation } from "@/components/location/LocationProvider";
import { isSavedMediaVariantUrl } from "@/lib/images/resolveMediaAssetUrl";
import { resolveListingCardImageUrl } from "@/lib/listingPhotos";
import { getListingCategoryPlaceholder } from "@/lib/taxonomy/placeholders";
import { getCustomerListingUrl, getListingUrl } from "@/lib/ids/publicRefs";
import { sortListingsByAvailability } from "@/lib/inventory";
import HomeSectionContainer from "@/components/home/HomeSectionContainer";
import { calculateListingPricing } from "@/lib/pricing";
import { withLocationHref } from "@/lib/location";

type TrendingListingsSectionProps = {
  mode?: BrowseMode;
  listings?: ListingSummary[];
  city?: string | null;
  title?: string;
  subtitle?: string;
  limit?: number;
  minItems?: number;
  variant?: "featured" | "new";
  excludeListingIds?: Array<string | null | undefined>;
};

function formatPrice(value?: number | string | null): string {
  if (value === null || value === undefined || value === "") return "Price TBD";
  const number = Number(value);
  if (Number.isNaN(number)) return "Price TBD";
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getDisplayPriceCents(listing: ListingSummary) {
  const finalPriceCents = Number(listing?.finalPriceCents);
  if (Number.isFinite(finalPriceCents) && finalPriceCents > 0) return finalPriceCents;
  return calculateListingPricing(listing?.price).finalPriceCents;
}

function formatPriceCents(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "Price TBD";
  return formatPrice(value / 100);
}

function getListingKey(listing: ListingSummary) {
  return String(listing?.public_id || listing?.id || "").trim();
}

function getTime(value?: string | null) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function getFreshListings({
  listings,
  excludeListingIds,
  limit,
  minItems,
}: {
  listings: ListingSummary[];
  excludeListingIds: Set<string>;
  limit: number;
  minItems: number;
}) {
  const sorted = [...listings].sort((left, right) => {
    const rightTime = Math.max(getTime(right?.inventory_last_updated_at), getTime(right?.created_at));
    const leftTime = Math.max(getTime(left?.inventory_last_updated_at), getTime(left?.created_at));
    return rightTime - leftTime;
  });
  const seen = new Set<string>();
  const dedupe = (listing: ListingSummary) => {
    const key = getListingKey(listing);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  };
  const distinct = sorted.filter((listing) => !excludeListingIds.has(getListingKey(listing))).filter(dedupe);
  const targetCount = Math.min(limit, Math.max(0, minItems), sorted.length);

  if (distinct.length >= targetCount) return distinct.slice(0, limit);

  const reused = sorted.filter((listing) => excludeListingIds.has(getListingKey(listing)));
  const rotatedReuse =
    reused.length > 1 ? [...reused.slice(Math.ceil(reused.length / 2)), ...reused.slice(0, Math.ceil(reused.length / 2))] : reused;

  return [...distinct, ...rotatedReuse.filter(dedupe)].slice(0, targetCount);
}

export default function TrendingListingsSection({
  mode = "public",
  listings = [],
  city,
  title,
  subtitle,
  limit = 8,
  minItems,
  variant = "featured",
  excludeListingIds = [],
}: TrendingListingsSectionProps) {
  const { location } = useLocation();
  const safeListings = useMemo(
    () => (Array.isArray(listings) ? listings : []),
    [listings]
  );
  const excludedKeys = useMemo(
    () => new Set(excludeListingIds.map((id) => String(id || "").trim()).filter(Boolean)),
    [excludeListingIds]
  );
  const visibleListings = useMemo(() => {
    const effectiveLimit =
      variant === "featured" ? Math.min(Math.max(limit, 0), 4) : limit;
    if (variant === "new") {
      return getFreshListings({
        listings: sortListingsByAvailability(safeListings),
        excludeListingIds: excludedKeys,
        limit: effectiveLimit,
        minItems: minItems ?? 5,
      });
    }
    return safeListings.slice(0, effectiveLimit);
  }, [excludedKeys, limit, minItems, safeListings, variant]);

  const resolvedTitle = useMemo(() => {
    if (title) return title;
    if (variant === "new") return "Fresh finds";
    const trimmedCity = String(city || "").trim();
    return trimmedCity ? `Featured in ${trimmedCity}` : "Featured near you";
  }, [city, title, variant]);

  const resolvedSubtitle = useMemo(() => {
    const trimmed = String(subtitle || "").trim();
    return trimmed || null;
  }, [subtitle]);

  const viewAllHref = withLocationHref("/listings", location);
  const isNewSection = variant === "new";

  if (!visibleListings.length) return null;

  return (
    <section
      className={
        isNewSection
          ? "relative z-20 w-full bg-[#f8f7fb] pb-5 pt-4 md:pb-6 md:pt-5"
          : "relative z-20 w-full bg-[#f5f4f7] pb-4 pt-4 md:pb-5 md:pt-5"
      }
      data-homepage-discovery={isNewSection ? "fresh" : "compact"}
    >
      <HomeSectionContainer className="px-4 sm:px-6 md:px-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {isNewSection ? (
              <p className="mb-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Recently added
              </p>
            ) : null}
            <h2
              className={
                isNewSection
                  ? "text-[1.55rem] font-semibold tracking-[-0.04em] text-slate-900 sm:text-[1.7rem]"
                  : "text-[1.2rem] font-semibold tracking-[-0.035em] text-slate-900 sm:text-[1.3rem]"
              }
            >
              {resolvedTitle}
            </h2>
            {resolvedSubtitle ? (
              <p className="mt-1 text-sm text-slate-500">{resolvedSubtitle}</p>
            ) : null}
          </div>

          {!isNewSection ? (
            <Link
              href={viewAllHref}
              prefetch={false}
              className="inline-flex items-center justify-center text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a4c9340] focus-visible:ring-offset-0 focus-visible:ring-offset-[#faf6f0]"
            >
              View all listings →
            </Link>
          ) : null}
        </div>

        <div
          data-testid="homepage-listings-grid"
          role="region"
          aria-label={
            isNewSection
              ? "Recently added listings"
              : "Featured local listings"
          }
          tabIndex={0}
          className={
            isNewSection
              ? "-mx-4 -mb-6 -mt-2 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto scroll-smooth pb-8 pt-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              : "-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-1.5 pt-0.5 [overscroll-behavior-inline:contain] [-ms-overflow-style:none] [scrollbar-width:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8c73bb59] [&::-webkit-scrollbar]:hidden sm:-mx-6 sm:px-6 md:-mx-8 md:px-8"
          }
        >
          {isNewSection ? <div className="w-4 min-w-4 shrink-0" aria-hidden="true" /> : null}
          {visibleListings.map((listing, index) => {
            const href =
              mode === "customer" ? getCustomerListingUrl(listing) : getListingUrl(listing);
            const imageSrc =
              resolveListingCardImageUrl(listing) || getListingCategoryPlaceholder(listing);
            const businessName =
              String(listing?.business_name || "").trim() || "Local business";
            const displayPriceCents = getDisplayPriceCents(listing);
            const displayPrice =
              displayPriceCents > 0 ? formatPriceCents(displayPriceCents) : formatPrice(listing.price);

            return (
              <Link
                key={listing.public_id || listing.id || `${listing.title}-${index}`}
                href={href}
                prefetch={false}
                className={
                  isNewSection
                    ? "group flex h-full w-[42vw] min-w-[42vw] snap-start flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-200 ease-out hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_8px_18px_rgba(15,23,42,0.07)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8c73bb59] focus-visible:ring-offset-0 focus-visible:ring-offset-[#faf6f0] sm:w-[190px] sm:min-w-[190px] md:w-[210px] md:min-w-[210px] lg:w-[220px] lg:min-w-[220px]"
                    : "group flex h-[94px] w-[230px] min-w-[230px] snap-start overflow-hidden rounded-xl border border-slate-200/75 bg-white/90 shadow-[0_1px_2px_rgba(15,23,42,0.035)] transition duration-200 ease-out hover:border-slate-300 hover:bg-white hover:shadow-[0_7px_18px_rgba(15,23,42,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8c73bb59] sm:w-[270px] sm:min-w-[270px] md:h-[102px] md:w-[292px] md:min-w-[292px]"
                }
              >
                <div
                  className={
                    isNewSection
                      ? "relative aspect-[5/3.6] w-full overflow-hidden bg-slate-50/60"
                      : "relative h-full w-[92px] shrink-0 overflow-hidden bg-slate-50/70 md:w-[104px]"
                  }
                >
                  <Image
                    src={imageSrc}
                    alt={listing.title || "Listing"}
                    fill
                    sizes={
                      isNewSection
                        ? "(max-width: 767px) 42vw, 220px"
                        : "(max-width: 767px) 92px, 104px"
                    }
                    className="object-contain object-center p-2 transition-transform duration-200 ease-out group-hover:scale-105 sm:p-2.5"
                    unoptimized={isSavedMediaVariantUrl(imageSrc)}
                  />
                </div>

                <div
                  className={
                    isNewSection
                      ? "flex h-[108px] shrink-0 flex-col px-3.5 py-3 sm:h-[112px] sm:px-4 sm:py-3.5"
                      : "flex min-w-0 flex-1 flex-col justify-center px-3 py-2.5 md:px-3.5"
                  }
                >
                  <p
                    className={
                      isNewSection
                        ? "whitespace-nowrap text-[15px] font-semibold tracking-[-0.02em] text-slate-950 md:text-base"
                        : "whitespace-nowrap text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950"
                    }
                  >
                    {displayPrice}
                  </p>
                  <h3
                    className={
                      isNewSection
                        ? "mt-1 line-clamp-2 text-sm font-medium leading-tight tracking-[-0.01em] text-slate-800"
                        : "mt-0.5 line-clamp-1 text-[0.79rem] font-medium leading-tight tracking-[-0.01em] text-slate-800"
                    }
                  >
                    {listing.title || "Untitled listing"}
                  </h3>
                  <p
                    className={
                      isNewSection
                        ? "mt-1 line-clamp-1 text-xs text-slate-400"
                        : "mt-1 line-clamp-1 text-[0.7rem] text-slate-500"
                    }
                  >
                    {businessName}
                  </p>
                </div>
              </Link>
            );
          })}
          {isNewSection ? <div className="w-4 min-w-4 shrink-0" aria-hidden="true" /> : null}
        </div>
      </HomeSectionContainer>
    </section>
  );
}
